/**
 * CAT-001 — Catalog List Component
 * Displays listing cards with loading, error, and empty states
 * Uses FavoriteButton component for favorites (Edge-first, offline-first)
 * Memoized ListingCard for optimal re-render performance and offline support
 */

import { useRef, useEffect, useState, useCallback, memo } from 'react';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import type { ListingCard } from '@/lib/api';
import nobodyImage from '@/assets/nobody.png';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { RefreshCw, ArrowUp } from 'lucide-react';
import { FavoriteButton } from '@/components/features/FavoriteButton';
import { useFavoritesStore } from '@/lib/services/useFavoritesStore';
import { ImageSlider } from '@/components/features/ImageSlider';

interface CatalogListProps {
  items: ListingCard[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  onRetry: () => void;
  onLoadMore: () => void;
  onResetToStart?: () => void;
}

const CatalogList = ({
  items,
  isLoading,
  isLoadingMore,
  error,
  hasMore,
  onRetry,
  onLoadMore,
  onResetToStart,
}: CatalogListProps) => {
  const loaderRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Find the scrollable parent container
  const getScrollContainer = useCallback(() => {
    // The scrollable container in MainCatalog has class "overflow-y-auto"
    return document.querySelector('.overflow-y-auto') as HTMLElement | null;
  }, []);

  // Show/hide scroll to top button
  useEffect(() => {
    const container = getScrollContainer();
    if (!container) {
      // Fallback to window scroll
      const handleWindowScroll = () => {
        setShowScrollTop(window.scrollY > 400);
      };
      window.addEventListener('scroll', handleWindowScroll);
      return () => window.removeEventListener('scroll', handleWindowScroll);
    }

    const handleScroll = () => {
      setShowScrollTop(container.scrollTop > 400);
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [getScrollContainer]);

  // Infinite scroll observer - only load more, no circular reset
  useEffect(() => {
    if (isLoading || isLoadingMore || !hasMore) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = loaderRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMore, isLoading, isLoadingMore, onLoadMore]);

  const scrollToTop = useCallback(() => {
    const container = getScrollContainer();
    if (container) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [getScrollContainer]);

  // Loading state - skeleton cards
  if (isLoading) {
    return (
      <div data-testid="catalog-loading" className="flex flex-col gap-4 pb-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-card rounded-2xl overflow-hidden shadow-sm">
            <Skeleton className="w-full h-48" />
            <div className="p-4 space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        data-testid="catalog-error"
        className="flex-1 flex flex-col items-center justify-center px-6 py-8"
      >
        <p className="text-destructive font-medium text-center mb-4">
          {error}
        </p>
        <Button
          variant="outline"
          onClick={onRetry}
          className="gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Попробовать снова
        </Button>
      </div>
    );
  }

  // Empty state
  if (items.length === 0) {
    return (
      <div
        data-testid="catalog-empty"
        className="flex-1 flex flex-col items-center justify-center px-6 py-8"
      >
        <img src={nobodyImage} alt="Никого нет" className="w-52 h-auto mb-4" />
        <p
          className="text-text-primary font-medium text-[18px] leading-[1.3] text-center max-w-[280px] mb-2"
          style={{ fontFamily: '"CoFo Gothic", system-ui, sans-serif' }}
        >
          Но скоро появится, ведь мы все ищем себе друга по жизни
        </p>
      </div>
    );
  }

  // Cards list
  return (
    <div data-testid="catalog-list" className="flex flex-col gap-4 pb-4 relative">
      {items.map(item => (
        <ListingCardComponent 
          key={item.listing_id} 
          item={item} 
        />
      ))}

      {/* Load more / end of list */}
      <div ref={loaderRef} className="py-4 flex flex-col items-center gap-3">
        {isLoadingMore ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span>Загрузка...</span>
          </div>
        ) : hasMore ? (
          <span className="text-muted-foreground text-sm">Прокрутите вниз</span>
        ) : (
          <Button
            variant="outline"
            onClick={scrollToTop}
            className="gap-2"
          >
            <ArrowUp className="w-4 h-4" />
            Вернуться в начало
          </Button>
        )}
      </div>

      {/* Scroll to top button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-24 right-4 w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center z-50 animate-fade-in hover:scale-110 transition-transform"
          aria-label="Вернуться в начало"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

// Individual listing card component - memoized for performance
interface ListingCardComponentProps {
  item: ListingCard;
}

const ListingCardComponent = memo(function ListingCardComponent({ item }: ListingCardComponentProps) {
  const navigate = useSafeNavigate();
  const placeholderUrl = '/placeholder.svg';
  const store = useFavoritesStore();

  const isFavorited = store.isFavorited('listing', item.listing_id) || item.is_favorite;

  const handleCardClick = useCallback(() => {
    // Navigate to pet detail screen (PET-001)
    if (item.pet_id) {
      // Cache pet->listing mapping for Favorites action on Pet screen
      try {
        localStorage.setItem(`pet_listing:${item.pet_id}`, item.listing_id);
      } catch {
        // ignore
      }

      navigate(`/pet/${item.pet_id}`, {
        state: {
          listingId: item.listing_id,
          initialFavorite: isFavorited,
          from: '/catalog',
        },
      });
    }
  }, [item.pet_id, item.listing_id, isFavorited, navigate]);

  // Memoize images array to prevent ImageSlider re-renders
  const images = item.photos && item.photos.length > 0 
    ? item.photos 
    : (item.primary_photo_url ? [item.primary_photo_url] : [placeholderUrl]);

  return (
    <div
      data-testid={`listing-card-${item.listing_id}`}
      onClick={handleCardClick}
      className="bg-card rounded-2xl overflow-hidden shadow-sm cursor-pointer hover:shadow-md transition-all relative"
    >
      {/* Image with slider - swipe only for photos */}
      <div className="relative aspect-[4/3] bg-muted overflow-hidden rounded-2xl mx-3 mt-3">
        <ImageSlider
          images={images}
          alt={item.pet_description || 'Питомец'}
          showDots={true}
          className="rounded-2xl"
        />

        {/* Favorite button - using FavoriteButton component (Edge-first) */}
        <div className="absolute top-3 right-3 z-20">
          <FavoriteButton
            entityType="listing"
            entityId={item.listing_id}
            initialState={isFavorited}
            source="catalog"
            size={24}
            backgroundMode="onPhoto"
          />
        </div>
      </div>

      {/* Info */}
      <div className="p-4">
        {/* Pet name and interesting fact on one line */}
        <p
          className="text-text-primary text-sm mb-1 line-clamp-2"
          style={{ fontFamily: '"CoFo Gothic", system-ui, sans-serif' }}
        >
          {item.pet_name && (
            <span className="font-bold">{item.pet_name}</span>
          )}
          {item.pet_name && item.interesting_fact && ' '}
          {item.interesting_fact && (
            <span className="text-text-tertiary">{item.interesting_fact}</span>
          )}
        </p>

        {/* City with pin icon */}
        <div className="flex items-center gap-1.5 text-muted-foreground text-sm">
          <img src="/assets/icon-pin-brown.png" alt="" className="w-4 h-4" loading="lazy" />
          <span>{item.city?.name || 'Город не указан'}</span>
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if essential data changes
  return (
    prevProps.item.listing_id === nextProps.item.listing_id &&
    prevProps.item.is_favorite === nextProps.item.is_favorite &&
    prevProps.item.pet_name === nextProps.item.pet_name &&
    prevProps.item.interesting_fact === nextProps.item.interesting_fact
  );
});

export default CatalogList;
